import he from "he";
import parse from "html-react-parser";
import { useEffect, useMemo, useState } from "react";
import { LuLoaderCircle, LuTriangleAlert } from "react-icons/lu";
import * as SharedMessageTypes from "../../../../../../darvin-shared/schema/zod/agent/message-types.schema";
import { useProjectActions } from "../../../../../core_stores/useProjectStore";
import { HttpAssistantIsWorkingException } from "../../../../../model/httpException";
import { useSnackbar } from "../../../../../providers/SnackbarProvider";
import { useSubscriptionsModal } from "../../../../../providers/SubscriptionsModalProvider";
import { splitIntoWords } from "../../../../../services/textSplitterService";
import { ProjectChatAssistantMessage } from "../../../../../view_stores/chat_messages/chatModels";
import {
  useIsProjectProcessing,
  useProjectStatusActions,
} from "../../../../../view_stores/useProjectStatusStore";
import {
  ProjectAssistantMessageBase,
  RollbackBtnProps,
} from "./ProjectAssistantMessageBase";

interface ProjectAssistantErrorMessageProps {
  message: ProjectChatAssistantMessage;
  canFix: boolean;
  rollbackBtn?: RollbackBtnProps;
}

export function ProjectAssistantErrorMessage({
  message,
  canFix,
  rollbackBtn,
}: ProjectAssistantErrorMessageProps) {
  const isProjectProcessing = useIsProjectProcessing();

  const { sendMessage, fixCompilationErrorForMessage } = useProjectActions();
  const { open: openSubscriptionsModal } = useSubscriptionsModal();
  const { showSnackbar } = useSnackbar();
  const { notifyingSubmitting } = useProjectStatusActions();

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isSubmitting) {
      notifyingSubmitting();
    }
  }, [isSubmitting, notifyingSubmitting]);

  useEffect(() => {
    if (!isProjectProcessing) {
      setIsSubmitting(false);
    }
  }, [isProjectProcessing]);

  function createContent(): string {
    let content = "<strong>Error:</strong><br />";
    const errorType = (
      message.value as
        | SharedMessageTypes.ErrorContent
        | { type: "notEnoughTokens" }
        | { type: "runtime"; message: string }
    ).type;
    if (errorType === SharedMessageTypes.ErrorTypeEnum.Enum.compilation) {
      content += "The code could not be compiled successfully due to an error.";
    } else if (errorType === SharedMessageTypes.ErrorTypeEnum.Enum.technical) {
      content +=
        "The last attempted change could not be applied due to a technical issue, and the project may not function correctly.";
    } else if (errorType === "runtime") {
      content += "The app encountered a runtime error while running.";
    } else if (errorType === "notEnoughTokens") {
      content +=
        "You've run out of tokens. Wait for your monthly reset or upgrade your plan to continue. ";
    } else {
      content += he.encode(
        (message.value as SharedMessageTypes.ErrorContent).message
      );
    }
    return content;
  }

  const isCompilationError =
    (message.value as SharedMessageTypes.ErrorContent).type ===
    SharedMessageTypes.ErrorTypeEnum.Enum.compilation;

  const isRuntimeError = (message.value as any).type === "runtime";

  const isTechnicalError =
    (message.value as SharedMessageTypes.ErrorContent).type ===
    SharedMessageTypes.ErrorTypeEnum.Enum.technical;

  const isNotEnoughTokensError =
    (message.value as { type: "notEnoughTokens" }).type === "notEnoughTokens";

  // Memoize content creation to prevent infinite re-renders
  const currentContent = useMemo(() => createContent(), [message]);

  // Memoize the word splitting to avoid recalculation
  const words = useMemo(() => splitIntoWords(currentContent), [currentContent]);

  const onTryToFixClick = async () => {
    setIsSubmitting(true);
    if (message.id === "preview-error") {
      await sendMessage(
        `Fix runtime error: @@error_details@@${
          (message.value as SharedMessageTypes.ErrorContent).message
        }@@/error_details@@`,
        null
      );
    } else {
      try {
        await fixCompilationErrorForMessage(message.id);
      } catch (error) {
        if (error instanceof HttpAssistantIsWorkingException) {
          setIsSubmitting(false);
          showSnackbar(
            "Previous action is still in progress, try again later.",
            {
              type: "error",
              duration: 5000,
            }
          );
        }
      }
    }
  };

  const getActions = () => {
    if ((isCompilationError || isRuntimeError) && canFix) {
      return (
        <div className="flex items-center justify-end">
          <button
            className="project-assistant-error-message-try-to-fix bg-[#D6D9E7] dark:bg-dark-surface-tertiary/25
      border border-[#656A6E40] dark:border-[#373B40]
      rounded-full w-[87px] h-[34px]
      flex items-center justify-center
      hover:bg-[#FFFFFF] dark:hover:bg-dark-surface-tertiary
      transition-all duration-300"
            onClick={onTryToFixClick}
            disabled={isProjectProcessing || isSubmitting}
          >
            {isSubmitting ? (
              <LuLoaderCircle className="animate-spin" size={16} />
            ) : (
              "Try to fix"
            )}
          </button>
        </div>
      );
    } else if (isNotEnoughTokensError) {
      return (
        <div className="flex items-center justify-end">
          <button
            className="bg-[#D6D9E7] dark:bg-dark-surface-tertiary/25
      border border-[#656A6E40] dark:border-[#373B40]
      rounded-full px-4 py-1
      hover:bg-[#FFFFFF] dark:hover:bg-dark-surface-tertiary
      transition-all duration-300"
            onClick={openSubscriptionsModal}
          >
            Plans
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <ProjectAssistantMessageBase
      message={message}
      typeWriterAnimationSpeed={50}
      words={words}
      renderContent={(visibleWords) => (
        <span>{parse(words.slice(0, visibleWords).join(""))}</span>
      )}
      expandableMessage={
        isCompilationError || isTechnicalError || isRuntimeError
          ? {
              message: he.encode(
                (message.value as SharedMessageTypes.ErrorContent).message
              ),
              icon: (
                <LuTriangleAlert
                  size={20}
                  className="text-[#DBA213] dark:text-[#D8C640]"
                />
              ),
              canCopy: true,
              expandBtnText: {
                contentHiddenText: "Error details",
                contentVisibleText: "Hide details",
              },
            }
          : undefined
      }
      actions={getActions()}
      rollbackBtn={rollbackBtn}
    />
  );
}
